import { phoneticToMarathi } from '../utils/marathi.js';

const CACHE = new Map();
const MAX_CACHE_SIZE = 1000;

export const CANDIDATE_STATE_EVENT = 'gp-marathi-candidate-state-change';

let state = {
  visible: false,
  word: '',
  candidates: [],
  selectedIndex: 0,
  position: { top: 0, left: 0 },
  targetElement: null,
  wordRange: { start: 0, end: 0 },
};

const listeners = new Set();

export function getCandidateState() {
  return state;
}

export function subscribeCandidateState(listener) {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}

function notifyListeners() {
  listeners.forEach(fn => {
    try {
      fn(state);
    } catch {
      // Ignore listener error
    }
  });
}

export function updateCandidateState(partial) {
  state = { ...state, ...partial };
  notifyListeners();
}

export function hideCandidates() {
  if (!state.visible && !state.word && state.candidates.length === 0) return;
  state = {
    ...state,
    visible: false,
    word: '',
    candidates: [],
    selectedIndex: 0,
    targetElement: null,
    wordRange: { start: 0, end: 0 },
  };
  notifyListeners();
}

export const setNativeValue = (element, value) => {
  if (!element) return;
  const prototype =
    element instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
  setter?.call(element, value);
};

export function commitCandidate(index = state.selectedIndex, appendSpace = false) {
  const { targetElement, wordRange, candidates } = state;
  const selectedText = candidates[index];
  if (!targetElement || !selectedText) {
    hideCandidates();
    return false;
  }

  const currentVal = targetElement.value;
  const start = wordRange.start;
  const end = wordRange.end;
  const replacement = selectedText + (appendSpace ? ' ' : '');
  const nextVal = `${currentVal.slice(0, start)}${replacement}${currentVal.slice(end)}`;

  setNativeValue(targetElement, nextVal);
  const nextCursor = start + replacement.length;
  targetElement.setSelectionRange(nextCursor, nextCursor);
  targetElement.dispatchEvent(new Event('input', { bubbles: true }));
  targetElement.dispatchEvent(new Event('change', { bubbles: true }));

  hideCandidates();
  try {
    targetElement.focus();
  } catch {
    // Ignore focus error
  }
  return true;
}

export async function fetchMarathiCandidates(word, maxCandidates = 5) {
  const trimmed = (word || '').trim();
  if (!trimmed || !/[A-Za-z]/.test(trimmed)) {
    return [];
  }

  const cacheKey = trimmed.toLowerCase();
  if (CACHE.has(cacheKey)) {
    return CACHE.get(cacheKey);
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 1800);

    const url = `https://inputtools.google.com/request?text=${encodeURIComponent(
      trimmed
    )}&itc=mr-t-i0-und&num=${maxCandidates}&cp=0&cs=1&ie=utf-8&oe=utf-8&app=demopage`;

    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Google Input Tools HTTP ${response.status}`);
    }

    const data = await response.json();
    if (Array.isArray(data) && data[0] === 'SUCCESS' && data[1]?.[0]?.[1]) {
      const apiCandidates = data[1][0][1] || [];
      const combined = [...apiCandidates];
      if (!combined.includes(trimmed)) {
        combined.push(trimmed);
      }

      if (CACHE.size >= MAX_CACHE_SIZE) {
        const firstKey = CACHE.keys().next().value;
        CACHE.delete(firstKey);
      }
      CACHE.set(cacheKey, combined);
      return combined;
    }
  } catch {
    // Network failure or timeout -> fallback to offline phonetic rule
  }

  const fallback = phoneticToMarathi(trimmed);
  const fallbackList = fallback && fallback !== trimmed ? [fallback, trimmed] : [trimmed];
  return fallbackList;
}
